import type { CSSProperties, ReactNode } from 'react'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import { getComponentHeights } from '../utils'
import { ButtonActionButton } from './ButtonActionButton'
import {
  ButtonActionsAction,
  ButtonActionsAlign,
  ButtonActionsLayout,
  ButtonActionsUISchema,
} from './types'

const DEFAULT_GRID_COLUMNS = 2
const DEFAULT_ALIGN: ButtonActionsAlign = 'center'

const TILE_HORIZONTAL_CLASS: Record<ButtonActionsAlign, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
}

const TILE_VERTICAL_CLASS: Record<ButtonActionsAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
}

const GROUP_HORIZONTAL_JUSTIFY_CLASS: Record<ButtonActionsAlign, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
}

const GROUP_HORIZONTAL_ITEMS_CLASS: Record<ButtonActionsAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
}

const GROUP_HORIZONTAL_GRID_CLASS: Record<ButtonActionsAlign, string> = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
}

function getVisibleButtons(buttons: ButtonActionsAction[]) {
  return buttons.filter((button) => button.isVisible !== false)
}

function getGroupClassName(layout: ButtonActionsLayout, horizontalAlign: ButtonActionsAlign) {
  if (layout === 'column') {
    return clsx(
      'flex max-w-full flex-col flex-wrap justify-center gap-2',
      GROUP_HORIZONTAL_ITEMS_CLASS[horizontalAlign]
    )
  }
  if (layout === 'grid') {
    return clsx('grid max-w-full items-center gap-2', GROUP_HORIZONTAL_GRID_CLASS[horizontalAlign])
  }
  return clsx(
    'flex max-w-full flex-row flex-wrap items-center gap-2',
    GROUP_HORIZONTAL_JUSTIFY_CLASS[horizontalAlign]
  )
}

export function ButtonActionsContent({
  uiSchema,
  tileFilters,
}: {
  uiSchema: ButtonActionsUISchema
  tileFilters?: ReactNode
}) {
  const {
    title,
    description,
    emphasizeHeader,
    layout = 'row',
    columns = DEFAULT_GRID_COLUMNS,
    horizontalAlign = DEFAULT_ALIGN,
    verticalAlign = DEFAULT_ALIGN,
    buttons = [],
  } = uiSchema
  const visibleButtons = getVisibleButtons(buttons)
  const hasHeader = Boolean(title) && Boolean(tileFilters)
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const layoutStyle: CSSProperties | undefined =
    layout === 'grid'
      ? { gridTemplateColumns: `repeat(${Math.max(columns, 1)}, max-content)` }
      : undefined

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          {tileFilters}
        </ComponentHeader>
      )}
      <div
        className={clsx(
          'flex min-h-0 overflow-auto p-4',
          TILE_HORIZONTAL_CLASS[horizontalAlign],
          TILE_VERTICAL_CLASS[verticalAlign],
          { 'pt-5': hasHeader }
        )}
        style={{ height: contentHeight }}
      >
        <div className={getGroupClassName(layout, horizontalAlign)} style={layoutStyle}>
          {visibleButtons.map((action) => (
            <ButtonActionButton key={action.id} action={action} />
          ))}
        </div>
      </div>
    </div>
  )
}
